// src/pages/Glossary.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { FiSearch } from 'react-icons/fi';
import { GLOSSARY_SEED } from '../glossarySeed';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Glossary() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null); // highlighted term after a jump

  // Admin state
  const [adminPassword, setAdminPassword] = useState('');
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [editing, setEditing] = useState(null); // term object being edited (or {} for new)
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState(null);

  const termRefs = useRef({});

  useEffect(() => { loadTerms(); }, []);

  const loadTerms = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/glossary`);
      setTerms(res.data.terms || []);
    } catch (err) {
      setError('Could not load the glossary.');
    } finally {
      setLoading(false);
    }
  };

  // Build a lookup of every term + alias -> the canonical entry id, for linking.
  // Sorted by length (longest first) so multi-word terms match before short ones.
  const linkIndex = useMemo(() => {
    const entries = [];
    for (const t of terms) {
      // The target id: if this entry is an alias_of another, resolve to that one.
      entries.push({ name: t.term, id: t.id, aliasOf: t.alias_of });
      if (t.aliases) {
        t.aliases.split(',').map(a => a.trim()).filter(Boolean).forEach(a => {
          entries.push({ name: a, id: t.id, aliasOf: t.alias_of });
        });
      }
    }
    // Resolve alias_of targets to real ids by name.
    const byName = {};
    terms.forEach(t => { byName[t.term.toLowerCase()] = t.id; });
    entries.forEach(e => {
      if (e.aliasOf && byName[e.aliasOf.toLowerCase()]) {
        e.id = byName[e.aliasOf.toLowerCase()];
      }
    });
    // Sort longest name first.
    entries.sort((a, b) => b.name.length - a.name.length);
    return entries;
  }, [terms]);

  // Jump to a term: expand/scroll/highlight it.
  const jumpToTerm = (id) => {
    setActiveId(id);
    const el = termRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Clear the highlight after a moment.
    setTimeout(() => setActiveId(null), 2000);
  };

  // Render a definition with cross-links. Finds mentions of known terms and
  // turns them into clickable links (skips linking a term to itself).
  const renderLinkedDefinition = (text, selfId) => {
    if (!text) return null;
    // Build a combined regex of all term names (escaped), longest first.
    const names = linkIndex
      .filter(e => e.id !== selfId) // don't link a term to itself
      .map(e => e.name);
    if (names.length === 0) return text;

    const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');

    const parts = [];
    let lastIndex = 0;
    let match;
    const linkedThisName = new Set(); // only link first occurrence of each name
    while ((match = re.exec(text)) !== null) {
      const matched = match[0];
      const lowerKey = matched.toLowerCase();
      const entry = linkIndex.find(e => e.name.toLowerCase() === lowerKey && e.id !== selfId);
      if (!entry || linkedThisName.has(lowerKey)) {
        continue; // skip; will be rendered as plain text via the slice below
      }
      linkedThisName.add(lowerKey);
      // push preceding text
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      parts.push(
        <button
          key={`${match.index}-${matched}`}
          onClick={() => jumpToTerm(entry.id)}
          className="text-indigo-600 hover:text-indigo-800 underline decoration-dotted"
        >
          {matched}
        </button>
      );
      lastIndex = match.index + matched.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
  };

  // Filter by search.
  const filtered = terms.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.term.toLowerCase().includes(q) ||
      (t.definition && t.definition.toLowerCase().includes(q)) ||
      (t.aliases && t.aliases.toLowerCase().includes(q))
    );
  });

  // --- Admin actions ---
  const handleUnlock = async () => {
    setAdminError(null);
    try {
      await axios.post(`${API_URL}/api/admin/verify`, { password: adminPassword });
      setAdminUnlocked(true);
      setShowAdminPrompt(false);
    } catch {
      setAdminError('Incorrect password');
    }
  };

  const saveTerm = async () => {
    if (!editing.term?.trim() || !editing.definition?.trim()) {
      setAdminError('Term and definition are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/glossary/admin/add`, {
        password: adminPassword,
        id: editing.id,
        term: editing.term,
        definition: editing.definition,
        aliases: editing.aliases || '',
        alias_of: editing.alias_of || ''
      });
      setEditing(null);
      await loadTerms();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not save');
    }
  };

  const deleteTerm = async (id) => {
    if (!window.confirm('Delete this glossary term? This cannot be undone.')) return;
    try {
      await axios.post(`${API_URL}/api/glossary/admin/delete`, { password: adminPassword, id });
      await loadTerms();
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Could not delete');
    }
  };

  // One-time seed: loads the ~66 starter terms. Uses replaceAll so it's safe to
  // re-run (it clears and reloads rather than creating duplicates).
  const seedGlossary = async () => {
    const count = GLOSSARY_SEED.length;
    const msg = terms.length > 0
      ? `This will REPLACE all ${terms.length} current term(s) with the ${count} starter terms. Continue?`
      : `Load the ${count} starter garage-door terms?`;
    if (!window.confirm(msg)) return;
    setSeeding(true);
    setSeedMsg(null);
    try {
      const res = await axios.post(`${API_URL}/api/glossary/admin/import`, {
        password: adminPassword,
        terms: GLOSSARY_SEED,
        replaceAll: true
      });
      setSeedMsg(res.data.message || `Imported ${count} terms.`);
      await loadTerms();
    } catch (err) {
      setSeedMsg(err.response?.data?.error || 'Seeding failed');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-600">Loading glossary…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600 mb-3">{error}</p>
        <button onClick={loadTerms} className="bg-red-600 text-white px-4 py-2 rounded">Try Again</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Glossary</h2>
          <p className="text-gray-600">
            Garage door terms and definitions. Click any underlined term to jump to its definition.
          </p>
        </div>
        <div className="ml-4 shrink-0">
          {!adminUnlocked ? (
            <button onClick={() => { setShowAdminPrompt(!showAdminPrompt); setAdminError(null); }}
              className="text-sm text-gray-500 hover:text-gray-800 underline">Admin</button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">Admin mode</span>
              <button onClick={() => setEditing({})} className="text-sm text-indigo-600 underline">Add term</button>
              <button onClick={seedGlossary} disabled={seeding} className="text-sm text-indigo-600 underline disabled:opacity-50">
                {seeding ? 'Seeding…' : 'Seed starter terms'}
              </button>
              <button onClick={() => { setAdminUnlocked(false); setAdminPassword(''); }} className="text-sm text-gray-500 underline">Lock</button>
            </div>
          )}
        </div>
      </div>

      {/* Admin password prompt */}
      {showAdminPrompt && !adminUnlocked && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 max-w-sm">
          <label className="block text-sm font-medium text-gray-700 mb-1">Admin password</label>
          <input type="password" value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleUnlock(); }}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          {adminError && <p className="text-red-600 text-sm mt-2">{adminError}</p>}
          <button onClick={handleUnlock} className="mt-3 bg-indigo-600 text-white text-sm px-4 py-2 rounded">Unlock</button>
        </div>
      )}

      {/* Seed message */}
      {seedMsg && adminUnlocked && (
        <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded p-3 text-sm text-indigo-800">{seedMsg}</div>
      )}

      {/* Search */}
      <div className="mb-6 relative max-w-xl">
        <FiSearch className="absolute left-3 top-3 text-gray-400" size={20} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search terms and definitions…"
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <p className="text-gray-600 mb-4 text-sm">{filtered.length} term{filtered.length === 1 ? '' : 's'}</p>

      {/* Term list */}
      <div className="space-y-3">
        {filtered.map(t => (
          <div
            key={t.id}
            ref={(el) => { termRefs.current[t.id] = el; }}
            className={`bg-white rounded-lg shadow p-4 transition ${activeId === t.id ? 'ring-2 ring-indigo-400' : ''}`}
          >
            <div className="flex items-start justify-between">
              <h3 className="font-semibold text-gray-900">
                {t.term}
                {t.is_stub && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">needs definition</span>}
              </h3>
              {adminUnlocked && (
                <div className="flex gap-3 shrink-0 ml-3">
                  <button onClick={() => setEditing(t)} className="text-xs text-indigo-600 underline">Edit</button>
                  <button onClick={() => deleteTerm(t.id)} className="text-xs text-red-600 underline">Delete</button>
                </div>
              )}
            </div>
            {t.aliases && (
              <p className="text-xs text-gray-500 mt-1">Also: {t.aliases.split(',').map(a => a.trim()).join(', ')}</p>
            )}
            <p className="text-gray-700 mt-2 leading-relaxed">
              {renderLinkedDefinition(t.definition, t.id)}
            </p>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-600">No terms match your search.</div>
        )}
      </div>

      {/* Edit / add modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">{editing.id ? 'Edit term' : 'Add term'}</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
            <input value={editing.term || ''} onChange={(e) => setEditing({ ...editing, term: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Definition</label>
            <textarea value={editing.definition || ''} onChange={(e) => setEditing({ ...editing, definition: e.target.value })}
              rows={5} className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Also known as (comma-separated, optional)</label>
            <input value={editing.aliases || ''} onChange={(e) => setEditing({ ...editing, aliases: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" placeholder="e.g. Spring Assembly, Torsion Assembly" />
            {adminError && <p className="text-red-600 text-sm mb-2">{adminError}</p>}
            <div className="flex gap-2">
              <button onClick={saveTerm} className="bg-indigo-600 text-white text-sm px-4 py-2 rounded">Save</button>
              <button onClick={() => setEditing(null)} className="text-sm text-gray-600 px-4 py-2 rounded border border-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Glossary;
